import t from "@/lang/lang";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import { Address } from "@/services/backend/addresses/types/address";
import { FieldErrors } from "@/shared/lib/errors/validation-error";
import { joinFieldName } from "@/shared/lib/errors/err-validation-failed-context";
import { TextInputField } from "@/shared/components/field/input-field";

interface CustomerAddressCardProps {
  customerAddress: Address;
  onCustomerAddressChange: (customerAddress: Address) => void;
  title?: string;
  description?: string;
  /**
   * Additional errors, keyed relative to the address (e.g. "street"),
   * merged with the errors of the ErrValidationFailedContext.
   */
  fieldErrors?: FieldErrors;
  /**
   * The field name of the address in the ErrValidationFailedContext. Its fields
   * are read from and reset at `${fieldName}.<field>`, e.g.
   * "postalAddress.street".
   */
  fieldName?: string;
  disabled?: boolean;
}

export function CustomerAddressCard({
  customerAddress,
  onCustomerAddressChange,
  fieldErrors = undefined,
  fieldName = "postalAddress",
  title = t("Kundenadresse"),
  description = t("Adresse des Kunden"),
  disabled = false,
}: CustomerAddressCardProps) {
  const field = (name: keyof Address, label: string) => ({
    label,
    placeholder: label,
    text: customerAddress[name],
    onTextChange: (value: string) =>
      onCustomerAddressChange({ ...customerAddress, [name]: value }),
    fieldName: joinFieldName(fieldName, name),
    errors: fieldErrors?.[name],
    disabled,
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4 sm:grid-cols-2">
        <TextInputField {...field("company", t("Firma"))} />
        <TextInputField {...field("department", t("Abteilung"))} />
        <TextInputField {...field("contact", t("Ansprechpartner"))} />
        <TextInputField {...field("street", t("Straße"))} />
        <div className="flex space-x-4">
          <TextInputField
            {...field("postalCode", t("PLZ"))}
            fieldProps={{ className: "max-w-xs" }}
          />
          <TextInputField {...field("city", t("Stadt"))} />
        </div>
        <TextInputField {...field("country", t("Land"))} />
      </CardContent>
    </Card>
  );
}
